import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, X, Bold, Italic, Link, List } from 'lucide-react';

interface ThreadReplyFormProps {
  onSubmit: (content: string) => Promise<void>;
  onCancel: () => void;
  initialContent?: string;
  placeholder?: string;
}

export const ThreadReplyForm: React.FC<ThreadReplyFormProps> = ({
  onSubmit,
  onCancel,
  initialContent = '',
  placeholder = 'Write a reply...'
}) => {
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Focus the textarea when the component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);
  
  const handleSubmit = async () => {
    if (!content.trim()) return;
    
    try {
      setIsSubmitting(true);
      await onSubmit(content);
      setContent('');
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };
  
  const insertMarkdown = (prefix: string, suffix: string = '') => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      
      const newContent = 
        content.substring(0, start) + 
        prefix + 
        selectedText + 
        suffix + 
        content.substring(end);
      
      setContent(newContent);
      
      // Set cursor position after insertion
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + prefix.length, 
          start + prefix.length + selectedText.length
        );
      }, 0);
    }
  };
  
  return (
    <motion.div
      className="bg-[#1a2533]/80 backdrop-blur-lg border border-white/10 rounded-lg p-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
    >
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full p-2 bg-[#141C28]/60 text-white rounded border border-white/10 focus:border-[#c9fffc] focus:outline-none resize-y min-h-[100px]"
      />
      
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => insertMarkdown('**', '**')}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
            title="Bold (Ctrl+B)"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('*', '*')}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
            title="Italic (Ctrl+I)"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('[', '](url)')}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
            title="Link (Ctrl+K)"
          >
            <Link size={16} />
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('- ')}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
            title="List"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
            title="Markdown Help"
          >
            ?
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-white rounded hover:bg-[#232f3e] transition-colors"
          >
            <X size={16} />
          </button>
          <motion.button
            type="button"
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!content.trim() || isSubmitting}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Send size={16} />
            Reply
          </motion.button>
        </div>
      </div>
      
      {showMarkdownHelp && (
        <div className="mt-3 p-3 bg-[#141C28] text-sm text-gray-300 rounded">
          <p className="font-medium text-white mb-1">Markdown Shortcuts:</p>
          <ul className="space-y-1">
            <li><code>**bold**</code> - <strong>bold</strong></li>
            <li><code>*italic*</code> - <em>italic</em></li>
            <li><code>[link](url)</code> - <a href="#" className="text-[#c9fffc]">link</a></li>
            <li><code>- item</code> - bulleted list</li>
            <li><code>1. item</code> - numbered list</li>
            <li><code>`code`</code> - <code>code</code></li>
            <li><code>Ctrl+Enter</code> - submit reply</li>
          </ul>
        </div>
      )}
    </motion.div>
  );
};
